import React, { useState, useEffect } from "react";
import Layout from "../../components/layout";
import { useDispatch } from "react-redux";
import { showLoading, hideLoading } from "../../redux/alertsSlice";
import { Col, Form, Row, Select, Button, message, Typography } from "antd";
import axios from "axios";
import { CalendarOutlined, PlusCircleOutlined, AppstoreOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;
const { Option } = Select;

/* ─── Tokens ─────────────────────────────────────────────────── */
const EMERALD    = "#0d9488";
const TEAL       = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE      = "#0f172a";
const SLATE_MID  = "#334155";
const MUTED      = "#94a3b8";
const SURFACE    = "#f8fafc";
const WHITE      = "#ffffff";

/* ─── Day data ────────────────────────────────────────────────── */
const standardDays = [
  "Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday",
  "By Appointment",
  "4th or 5th Saturday of the Month",
  "2nd or 3rd Tuesday of the Month",
];
const weekdays       = ["Monday to Friday"];
const everyTwoWeeks  = [
  "Every Two Weeks Monday","Every Two Weeks Tuesday","Every Two Weeks Wednesday",
  "Every Two Weeks Thursday","Every Two Weeks Friday","Every Two Weeks Saturday","Every Two Weeks Sunday",
];
const specialMonthly = [
  "Once a Month (Thursday)",
  "Third or Fourth Sunday of the Month",
  "Second to Last Saturday of Every Month",
  "Every Second Tuesday of the Month",
];
const dayCategories  = ["Standard","Alternate","Last","Everyday","Weekday","Every Two Weeks","Special Monthly"];

/* ─── Style injection ─────────────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("clinic-day-styles")) return;
  const el = document.createElement("style");
  el.id = "clinic-day-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

    .cd-root { font-family: 'DM Sans', sans-serif; background: ${SURFACE}; min-height: 100vh; }
    .cd-wrap { max-width: 680px; margin: 0 auto; padding: 32px 24px 64px; }

    /* Hero */
    .cd-hero {
      background: linear-gradient(135deg, ${SLATE} 0%, ${SLATE_MID} 100%);
      border-radius: 24px; padding: 36px 44px;
      position: relative; overflow: hidden; margin-bottom: 32px;
    }
    .cd-hero::before {
      content: ''; position: absolute; top: -80px; right: -80px;
      width: 300px; height: 300px; border-radius: 50%;
      background: radial-gradient(circle, ${TEAL}44 0%, transparent 70%);
      filter: blur(32px); pointer-events: none;
    }
    .cd-hero::after {
      content: ''; position: absolute; bottom: -60px; left: -40px;
      width: 200px; height: 200px; border-radius: 50%;
      background: radial-gradient(circle, ${EMERALD}33 0%, transparent 70%);
      filter: blur(26px); pointer-events: none;
    }
    .cd-eyebrow {
      display: inline-block; padding: 4px 12px; border-radius: 20px;
      background: ${TEAL_LIGHT}; color: ${EMERALD};
      font-size: 11px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase;
      font-family: 'Sora', sans-serif; margin-bottom: 12px;
    }

    /* Card section */
    .cd-section {
      background: ${WHITE}; border-radius: 20px;
      box-shadow: 0 4px 20px rgba(0,0,0,.06);
      padding: 28px 28px 20px; margin-bottom: 20px;
    }
    .cd-section-head {
      display: flex; align-items: center; gap: 10px; margin-bottom: 24px;
      padding-bottom: 16px; border-bottom: 1.5px solid #f1f5f9;
    }
    .cd-section-icon {
      width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
      background: ${TEAL_LIGHT}; color: ${EMERALD};
      display: flex; align-items: center; justify-content: center; font-size: 16px;
    }

    /* Form */
    .cd-root .ant-form-item { margin-bottom: 20px !important; }
    .cd-root .ant-form-item-label { padding-bottom: 4px !important; line-height: 1 !important; }
    .cd-root .ant-form-item-label > label {
      font-size: 12px !important; font-weight: 600 !important;
      color: ${SLATE_MID} !important; letter-spacing: .2px;
      height: 20px !important; line-height: 20px !important;
    }
    .cd-root .ant-form-item-explain { min-height: 0 !important; font-size: 11px !important; }

    /* Select (single) */
    .cd-root .ant-select-selector {
      border-radius: 10px !important; height: 44px !important;
      border: 1.5px solid #e2e8f0 !important; padding: 0 12px !important;
      display: flex !important; align-items: center !important;
      font-family: 'DM Sans', sans-serif !important; font-size: 13px !important;
      box-sizing: border-box !important;
      transition: border-color .2s, box-shadow .2s !important;
    }
    .cd-root .ant-select-selection-item,
    .cd-root .ant-select-selection-placeholder {
      line-height: 42px !important;
    }
    .cd-root .ant-select-focused .ant-select-selector,
    .cd-root .ant-select-selector:hover {
      border-color: ${TEAL} !important; box-shadow: 0 0 0 3px ${TEAL}22 !important;
    }
    .cd-root .ant-select-disabled .ant-select-selector {
      background: ${SURFACE} !important; color: ${MUTED} !important;
      cursor: not-allowed !important;
    }

    /* Hint box */
    .cd-hint {
      background: ${TEAL_LIGHT}; border-radius: 12px; padding: 12px 16px;
      display: flex; align-items: flex-start; gap: 10px; margin-bottom: 24px;
    }
    .cd-hint-dot {
      width: 6px; height: 6px; border-radius: 50%;
      background: ${EMERALD}; flex-shrink: 0; margin-top: 5px;
    }

    /* Submit */
    .cd-submit {
      height: 52px !important; width: 100% !important;
      border-radius: 14px !important;
      font-size: 15px !important; font-weight: 700 !important;
      font-family: 'Sora', sans-serif !important;
      background: linear-gradient(135deg, ${EMERALD}, ${TEAL}) !important;
      border: none !important; color: ${WHITE} !important;
      box-shadow: 0 8px 20px ${TEAL}44 !important;
      transition: transform .2s, box-shadow .2s !important;
    }
    .cd-submit:hover {
      transform: translateY(-2px) !important;
      box-shadow: 0 14px 32px ${TEAL}55 !important;
    }
    .cd-submit:disabled, .cd-submit.ant-btn-disabled {
      opacity: .55 !important; transform: none !important; cursor: not-allowed !important;
    }

    /* Animations */
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(16px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .cd-fade   { animation: fadeUp .5s ease both; }
    .cd-fade-1 { animation-delay: .05s; }
    .cd-fade-2 { animation-delay: .12s; }
  `;
  document.head.appendChild(el);
};

/* ─── Component ───────────────────────────────────────────────── */
const CreateClinicDay = () => {
  const dispatch = useDispatch();
  const [selectedCategory, setSelectedCategory] = useState("Standard");
  const [selectedDay, setSelectedDay]           = useState("");

  useEffect(() => { injectStyles(); }, []);

  const getDaysByCategory = () => {
    if (selectedCategory === "Standard")        return standardDays;
    if (selectedCategory === "Everyday")        return ["Everyday"];
    if (selectedCategory === "Weekday")         return weekdays;
    if (selectedCategory === "Special Monthly") return specialMonthly;
    if (selectedCategory === "Every Two Weeks") return everyTwoWeeks;
    return standardDays.map(day => `${selectedCategory} ${day}`);
  };

  const createClinicDay = async () => {
    if (!selectedDay) { message.error("Please select a clinic day."); return; }
    try {
      dispatch(showLoading());
      await axios.post("/api/user/create-clinic-day", { name: selectedDay });
      dispatch(hideLoading());
      message.success(`Clinic day "${selectedDay}" created successfully!`);
      setSelectedDay("");
    } catch (error) {
      dispatch(hideLoading());
      message.error(error.response?.data?.message || "Error creating clinic day.");
    }
  };

  return (
    <Layout>
      <div className="cd-root">
        <div className="cd-wrap">

          {/* Hero */}
          <div className="cd-hero cd-fade">
            <div style={{ position: "relative", zIndex: 1 }}>
              <div className="cd-eyebrow">
                <CalendarOutlined style={{ marginRight: 5 }} />Configuration
              </div>
              <Title level={2} style={{ margin: "6px 0 4px", fontFamily: "'Sora', sans-serif", color: WHITE, fontWeight: 800 }}>
                Create Clinic Day
              </Title>
              <Text style={{ color: MUTED, fontSize: 14 }}>
                Select a category and define the specific clinic day schedule
              </Text>
            </div>
          </div>

          {/* Form card */}
          <div className="cd-section cd-fade cd-fade-1">
            <div className="cd-section-head">
              <div className="cd-section-icon"><AppstoreOutlined /></div>
              <Title level={5} style={{ margin: 0, fontFamily: "'Sora', sans-serif", color: SLATE, fontWeight: 700 }}>
                Day Configuration
              </Title>
            </div>

            <Form layout="vertical" requiredMark={false}>
              <Row gutter={[20, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item label="Day Category">
                    <Select
                      placeholder="Select a category"
                      value={selectedCategory}
                      onChange={(value) => { setSelectedCategory(value); setSelectedDay(""); }}
                    >
                      {dayCategories.map(cat => (
                        <Option key={cat} value={cat}>{cat}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Select Clinic Day">
                    <Select
                      placeholder="Select a clinic day"
                      value={selectedDay || undefined}
                      onChange={setSelectedDay}
                      disabled={!selectedCategory}
                    >
                      {getDaysByCategory().map(day => (
                        <Option key={day} value={day}>{day}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {/* Hint */}
              <div className="cd-hint">
                <div className="cd-hint-dot" />
                <Text style={{ color: EMERALD, fontSize: 12, lineHeight: 1.5 }}>
                  Choose a category first to filter available day options. The created clinic day will be available when scheduling doctors.
                </Text>
              </div>

              <Button
                className="cd-submit"
                onClick={createClinicDay}
                disabled={!selectedDay}
                icon={<PlusCircleOutlined />}
              >
                Create Clinic Day
              </Button>
            </Form>
          </div>

        </div>
      </div>
    </Layout>
  );
};

export default CreateClinicDay;